import type { PricingTable } from './types';

export const STANDARD_PRICING_TABLE: PricingTable = {
    id: 'standard',
    options: [
        {
            id: 'essential',
            display_name: 'Essential',
            display_as_package: true,
            overview: [
                {
                    name: '1 site maximum',
                    details: 'This package comes with a 1 site maximum',
                },
                {
                    name: '1 user maximum',
                    details: 'This package comes with a 1 user maximum',
                },
                { name: 'Up to 5,000 contacts' },
                { name: 'Awesome support' },
                { name: 'Unlimited stories \u0026 campaigns' },
                { name: 'Full Analytics' },
                { name: 'Prezly Branded' },
                { name: 'Single Language/Culture' },
            ],
            prices: {
                month: {
                    eur: 10000,
                    usd: 12000,
                },
                year: {
                    eur: 96000,
                    usd: 120000,
                },
            },
        },
        {
            id: 'standard',
            display_name: 'Standard',
            overview: [
                {
                    name: '1 site included',
                    details: 'Extra sites can be purchased at 40/month',
                },
                {
                    name: '1 user included',
                    details: 'Extra users can be purchased',
                },
                { name: 'Up to 10,000 contacts' },
                { name: 'Awesome support' },
                { name: 'Unlimited stories \u0026 campaigns' },
                { name: 'Full Analytics' },
                { name: 'White-label sites' },
                { name: 'Localisation and Auto-translations' },
                { name: 'Your own domain' },
            ],
            prices: {
                month: { eur: 15000, usd: 17500 },
                year: { eur: 150000, usd: 180000 },
            },
        },
        {
            id: 'enterprise',
            display_name: 'Enterprise',
            display_as_package: true,
            overview: [
                {
                    name: 'Everything in Standard',
                },
                {
                    name: 'Custom site and user limits',
                },
                {
                    name: 'Premium Onboarding',
                    details: 'We help you import your media lists, set-up your site...',
                },
                {
                    name: '24/7 support, live training, +',
                },
                {
                    name: 'Custom site layout',
                    details: 'Customise the site to make it look like your website/brand.',
                },
                {
                    name: 'Single Sign-On',
                },
                {
                    name: '99% Uptime SLA',
                },
            ],
        },
    ],
    rows: [
        {
            display_name: 'Sites',
            area: 'Usage',
            is_key_item: true,
            cells: {
                essential: 'Single Site',
                standard: '1 Included, 40/month per site',
            },
        },
        {
            display_name: 'Stories',
            area: 'Usage',
            is_key_item: true,
            cells: {
                essential: 'Unlimited',
                standard: 'Unlimited',
            },
        },
        {
            display_name: 'Version history',
            description: 'View and revert to previous versions of your stories.',
            link: 'https://www.prezly.com/help/story-version-history',
            area: 'Usage',
            is_key_item: true,
            cells: {
                essential: '7-day history',
                standard: '90-day history',
            },
        },
        {
            display_name: 'Contacts',
            description: 'Maximum contacts that can be stored in your CRM',
            area: 'Usage',
            is_key_item: true,
            cells: {
                essential: '5000',
                standard: '10000',
            },
        },
        {
            display_name: 'Site themes',
            description:
                'Create a modern site in seconds by choosing one of our professionally designed themes.',
            link: 'https://www.prezly.com/themes',
            area: 'Key features',
            is_key_item: true,
            cells: {
                essential: true,
                standard: true,
            },
        },
        {
            display_name: 'Contact importer',
            description:
                'Move your contacts into Prezly with just a few clicks by importing an Excel doc, CSV file \u2013 or just copy-paste.',
            area: 'Key features',
            is_key_item: true,
            cells: {
                essential: true,
                standard: true,
            },
        },
        {
            display_name: 'Multimedia embeds',
            description:
                'Embed videos, tables, galleries, social media posts, PDFs, Google Docs \u0026 attachments in your stories.',
            area: 'Key features',
            is_key_item: true,
            cells: {
                essential: true,
                standard: true,
            },
        },
        {
            display_name: 'Story publishing',
            description:
                'Publish your story as public, private, confidential \u2013 or schedule it for later.',
            area: 'Key features',
            is_key_item: true,
            cells: {
                essential: true,
                standard: true,
            },
        },
        {
            display_name: 'Story snippets',
            description:
                'Save time by creating content snippets you can reuse time and again \u2013 perfect for disclaimers \u0026 boilerplates.',
            area: 'Key features',
            is_key_item: true,
            cells: {
                essential: false,
                standard: true,
            },
        },
        {
            display_name: 'Site analytics',
            description:
                'Get insights into how many people are reading your stories and visiting your site.',
            link: 'https://www.prezly.com/feature/analytics',
            area: 'Key features',
            is_key_item: true,
            cells: {
                essential: true,
                standard: true,
            },
        },
        {
            display_name: 'Email campaigns \u0026 pitches',
            description:
                'Send targeted mass campaigns and one-to-one pitches in just a few clicks.',
            link: 'https://www.prezly.com/feature/campaigns',
            area: 'Key features',
            is_key_item: true,
            cells: {
                essential: true,
                standard: true,
            },
        },
        {
            display_name: 'Campaign reports',
            description:
                'See precisely how people engage with your story and use those insights to calibrate your workflow.',
            area: 'Key features',
            is_key_item: true,
            cells: {
                essential: true,
                standard: true,
            },
        },
        {
            display_name: 'Click reports',
            description:
                'See how the links in your email are performing and which links your recipients are clicking on.',
            area: 'Key features',
            is_key_item: true,
            cells: {
                essential: true,
                standard: true,
            },
        },
        {
            display_name: 'Live chat support',
            description: 'Get the support you need, whenever you need it.',
            link: 'https://www.prezly.com/feature/customer-love',
            area: 'Key features',
            is_key_item: true,
            cells: {
                essential: true,
                standard: true,
            },
        },
        {
            display_name: 'Custom domain',
            description: 'Link your own domain to your Prezly site.',
            link: 'https://www.prezly.com/help/setting-up-a-custom-domain',
            area: 'Key features',
            is_key_item: true,
            cells: {
                essential: false,
                standard: true,
            },
        },
        {
            display_name: 'Advanced SEO settings',
            description:
                'Add an extra channel to your distribution by having all your stories discoverable on Google.',
            area: 'Key features',
            is_key_item: true,
            cells: {
                essential: false,
                standard: true,
            },
        },
        {
            display_name: 'Log \u0026 store coverage',
            description: 'Link online and upload offline coverage straight into Prezly.',
            link: 'https://www.prezly.com/features/coverage',
            area: 'Key features',
            is_key_item: true,
            cells: {
                essential: true,
                standard: true,
            },
        },
        {
            display_name: 'User permissions',
            description:
                'Control who can access what with easy permissions settings for every member of your team.',
            area: 'Key features',
            is_key_item: true,
            cells: {
                essential: false,
                standard: false,
            },
        },
        {
            display_name: 'Whitelabel',
            description:
                'Remove any mention of us from your Prezly site and integrate it seamlessly into your website.',
            link: 'https://www.prezly.com/feature/whitelabel',
            area: 'Key features',
            is_key_item: true,
            cells: {
                essential: false,
                standard: true,
            },
        },
        {
            display_name: 'Media galleries',
            description: 'Create an unlimited gallery of media assets for your audience.',
            area: 'Sites',
            is_key_item: false,
            cells: {
                essential: true,
                standard: true,
            },
        },
        {
            display_name: 'Subscription form',
            description: 'Capture new contacts when they\u0027re most excited about your brand.',
            area: 'Sites',
            is_key_item: false,
            cells: {
                essential: true,
                standard: true,
            },
        },
        {
            display_name: 'API, JSON \u0026 RSS feeds',
            description:
                'Build on top of your Prezly stories or use feeds to integrate them into your website.',
            link: 'https://www.prezly.com/features/pr-portfolio',
            area: 'Sites',
            is_key_item: false,
            cells: {
                essential: true,
                standard: true,
            },
        },
        {
            display_name: 'Sites hub',
            description:
                'Create an online \u0022hub\u0022 to showcase the best stories from the sites in your account.',
            link: 'https://www.prezly.com/features/pr-portfolio',
            area: 'Sites',
            is_key_item: false,
            cells: {
                essential: false,
                standard: true,
            },
        },
        {
            display_name: 'Advanced site options',
            description: 'Add a link to your main site in the site header.',
            area: 'Sites',
            is_key_item: false,
            cells: {
                essential: false,
                standard: true,
            },
        },
        {
            display_name: 'Localization',
            description: 'Publish your story in more than 34 languages.',
            link: 'https://www.prezly.com/help/using-prezly/stories/supported-languages-in-prezly',
            area: 'Stories',
            is_key_item: false,
            cells: {
                essential: false,
                standard: true,
            },
        },
        {
            display_name: 'Add-on: AI translation',
            description:
                'Powered by DeepL, this add-on allows you to create instantaneous translations of your Prezly stories using artificial intelligence. The feature supports translation in 30 languages.',
            area: 'Stories',
            is_key_item: false,
            cells: {
                essential: false,
                standard: true,
            },
        },
        {
            display_name: 'Embargoed Stories',
            description: 'Control who can access your stories and when they go live.',
            area: 'Stories',
            is_key_item: false,
            cells: {
                essential: false,
                standard: true,
            },
        },
        {
            display_name: 'Social sharing',
            description: 'Make it easy for fans to share your story.',
            area: 'Stories',
            is_key_item: false,
            cells: {
                essential: true,
                standard: true,
            },
        },
        {
            display_name: 'Story pinning',
            description:
                'Highlight your most important story by pinning it to the top of your homepage.',
            area: 'Stories',
            is_key_item: false,
            cells: {
                essential: true,
                standard: true,
            },
        },
        {
            display_name: 'Categories',
            description:
                'Help visitors find what they\u0027re looking for by categorizing your stories.',
            link: 'https://www.prezly.com/help/using-prezly/stories/configuring-stories/categorizing-your-stories',
            area: 'Stories',
            is_key_item: false,
            cells: {
                essential: false,
                standard: true,
            },
        },
        {
            display_name: 'Contact tags \u0026 segments',
            description: 'Create and save reusable media lists using tags, segments and filters.',
            link: 'https://www.prezly.com/academy/best-practices/building-relationships-with-prezly',
            area: 'Contacts',
            is_key_item: false,
            cells: {
                essential: true,
                standard: true,
            },
        },
        {
            display_name: 'Contact notes',
            description: 'Collaborate with your team by leaving notes on contacts within the CRM.',
            area: 'Contacts',
            is_key_item: false,
            cells: {
                essential: true,
                standard: true,
            },
        },
        {
            display_name: 'Campaign scheduling',
            description: 'Set your emails to send automatically at a specified time and date.',
            area: 'Distribution',
            is_key_item: false,
            cells: {
                essential: true,
                standard: true,
            },
        },
        {
            display_name: 'Custom sender addresses',
            description: 'Send emails from your own email addresses.',
            area: 'Distribution',
            is_key_item: false,
            cells: {
                essential: false,
                standard: true,
            },
        },
        {
            display_name: 'Link coverage to contacts',
            description:
                'Link your coverage to your contacts for an easy overview of your biggest fans.',
            link: 'https://www.prezly.com/feature/coverage',
            area: 'Coverage',
            is_key_item: false,
            cells: {
                essential: true,
                standard: true,
            },
        },
        {
            display_name: 'Add-on: Coverage integrations',
            description:
                'Save time by pulling data from your coverage provider into Prezly. Supports Google Alerts, Belga, Auxipress, RSS and more.',
            area: 'Coverage',
            is_key_item: false,
            cells: {
                essential: {
                    eur: 'From 10€/month',
                    usd: 'From $10/month',
                },
                standard: {
                    eur: 'From 10€/month',
                    usd: 'From $10/month',
                },
            },
        },
        {
            display_name: 'Add-on: Media monitoring',
            description:
                'Automatically pull in news mentions from across 235,000 sources, 220 countries, and in 150 languages. Powered by Opoint.',
            link: 'https://www.prezly.com/feature/media-monitoring',
            area: 'Coverage',
            is_key_item: false,
            cells: {
                essential: {
                    eur: 'From 150€/month',
                    usd: 'From $150/month',
                },
                standard: {
                    eur: 'From 150€/month',
                    usd: 'From $150/month',
                },
            },
        },
        {
            display_name: 'Export coverage',
            description: 'Export all your logged coverage for easy reporting.',
            area: 'Coverage',
            is_key_item: false,
            cells: {
                essential: true,
                standard: true,
            },
        },
        {
            display_name: 'Sign-in with Google',
            description: 'Use Google Authentication to log in to your Prezly account.',
            area: 'Security',
            is_key_item: false,
            cells: {
                essential: true,
                standard: true,
            },
        },
        {
            display_name: 'Multiple accounts',
            description:
                'Use a single login for multiple accounts and switch between them at any time.',
            area: 'Security',
            is_key_item: false,
            cells: {
                essential: true,
                standard: true,
            },
        },
        {
            display_name: 'GDPR compliant',
            description: 'See how we keep your data safe and compliant with the GDPR.',
            area: 'More',
            is_key_item: false,
            cells: {
                essential: true,
                standard: true,
            },
        },
        {
            display_name: 'Extensive help documentation',
            area: 'More',
            description:
                'A handy product manual, whether you\u0027re starting out or on the hunt for power tips.',
            link: 'https://www.prezly.com/help',
            is_key_item: false,
            cells: {
                essential: true,
                standard: true,
            },
        },
        {
            display_name: 'Custom privacy policy link',
            area: 'More',
            description:
                'Customize your email footer and the cookie message that appears on your site(s).',
            is_key_item: false,
            cells: {
                essential: false,
                standard: true,
            },
        },
    ],
};

export const AGENCY_PRICING_TABLE: PricingTable = {
    id: 'agency',
    options: [
        {
            id: 'agency_essential',
            display_name: 'Agency Essential',
            display_as_package: true,
            description: 'Agency Essential',
            overview: [
                { name: '3 named users', details: 'Access for three different team members.' },
                { name: '10 sites included' },
                { name: 'Up to 10,000 contacts' },
                { name: 'Awesome support' },
                { name: 'Unlimited stories \u0026 campaigns' },
                { name: 'Full Analytics' },
                { name: 'Localisation and Auto-translations' },
                { name: 'Prezly branded' },
            ],
            prices: {
                month: { eur: 45000, usd: 52500 },
                year: { eur: 360000, usd: 420000 },
            },
        },
        {
            id: 'agency_standard',
            display_name: 'Agency Standard',
            display_as_package: true,
            description: 'Agency Standard',
            overview: [
                { name: '5 named users', details: 'Access for three different team members.' },
                { name: '20 sites included' },
                { name: 'Up to 20,000 contacts' },
                { name: 'Awesome support' },
                { name: 'Unlimited stories \u0026 campaigns' },
                { name: 'Full Analytics' },
                { name: 'Localisation and Auto-translations' },
                { name: 'Hub rooms \u0026 media galleries' },
                { name: 'User permissions, categories, +' },
                { name: 'Whitelabeled' },
                { name: 'Your own domain' },
            ],
            prices: {
                month: { eur: 60000, usd: 70000 },
                year: { eur: 540000, usd: 630000 },
            },
        },
        {
            id: 'enterprise',
            display_name: 'Agency Custom',
            display_as_package: true,
            overview: [
                {
                    name: 'All Agency Features',
                },
                {
                    name: 'Custom site and user limits',
                },
                { name: 'Support for +20k contacts' },
                {
                    name: 'Premium Onboarding',
                    details: 'We help you import your media lists, set-up your site...',
                },
                {
                    name: '24/7 support, live training, +',
                },
                {
                    name: 'Custom site layout',
                    details: 'Customise the site to make it look like your website/brand.',
                },
                {
                    name: 'Single Sign-On',
                },
                {
                    name: '99% Uptime SLA',
                },
            ],
        },
    ],
    rows: [],
};
